export type ToolType = 'chainsaw' | 'wedge' | 'hand' | null;
export type AppMode = 'PPE' | 'OHLEC' | 'SIMULATOR' | 'ASSESSMENT';

export const DEFAULT_BINDING_THRESHOLD = 200;

export interface StateData {
  activeMode: AppMode;
  activeTool: ToolType;
  showPerfWarnings: boolean;
  throttleOn: boolean;
  selectedPPEId: string;
  equippedPPE: Record<string, boolean>;
  featureFlags: {
    showTexture: boolean;
    showNodes: boolean;
    showEdges: boolean;
    showTension: boolean;
    globalTension: boolean;
    showGuidebarBox: boolean;
    showKerfBox: boolean;
    depthTest: boolean;
  };
  simulation: {
    gravity: number;
    bindingThreshold: number;
    currentPressure: number;
  };
  chainsawStuck: boolean;
  lastResetTime: number;
}

type Listener = (state: StateData) => void;

class Store {
  private state: StateData = {
    activeMode: 'SIMULATOR',
    activeTool: 'chainsaw',
    showPerfWarnings: false,
    throttleOn: false,
    chainsawStuck: false,
    lastResetTime: 0,
    selectedPPEId: 'hardhat',
    equippedPPE: {},
    featureFlags: {
      showTexture: true,
      showNodes: false,
      showEdges: false,
      showTension: false,
      globalTension: false,
      showGuidebarBox: false,
      showKerfBox: false,
      depthTest: true,
    },
    simulation: {
      gravity: 9.8,
      bindingThreshold: DEFAULT_BINDING_THRESHOLD,
      currentPressure: 0,
    }
  };

  private listeners: Listener[] = [];

  getState(): StateData {
    return this.state;
  }

  update(partial: Partial<StateData>) {
    let changed = false;
    for (const key in partial) {
      if (this.state[key as keyof StateData] !== partial[key as keyof StateData]) {
        changed = true;
        break;
      }
    }
    if (changed) {
      this.state = { ...this.state, ...partial };
      this.notify();
    }
  }

  updateFeature(flag: keyof StateData['featureFlags'], value: boolean) {
    if (this.state.featureFlags[flag] !== value) {
      this.state.featureFlags[flag] = value;
      this.notify();
    }
  }

  updateSimulation(key: keyof StateData['simulation'], value: number) {
    if (this.state.simulation[key] !== value) {
      this.state.simulation[key] = value;
      this.notify();
    }
  }

  subscribe(listener: Listener) {
    this.listeners.push(listener);
    listener(this.state);
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }

  private notify() {
    for (const listener of this.listeners) {
      listener(this.state);
    }
  }
}

export const UIState = new Store();
